import React, { useEffect, useMemo, useState } from "react";
import { Mail, Send, CheckCircle2, AlertCircle, Filter, RefreshCw, X, Search } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TEMPLATES = [
  { id: "invite", label: "Resend invite", needs: ["user"] },
  { id: "password_reset", label: "Password reset", needs: ["user"] },
  { id: "campaign_created", label: "New campaign created", needs: ["user", "campaign"] },
  { id: "new_comment", label: "New comment on campaign", needs: ["user", "campaign", "comments"] },
  { id: "status_updated", label: "Campaign status updated", needs: ["user", "campaign"] },
];

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso.slice(0, 16);
  }
}

function prettyTemplate(t) {
  return (t || "").replace(/_manual$/, "").replace(/_auto$/, " (auto)").replace(/_/g, " ");
}

export default function EmailManagementPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ template: "", recipient: "" });
  const [composeOpen, setComposeOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.template) params.set("template", filter.template);
      if (filter.recipient) params.set("recipient", filter.recipient);
      const { data } = await api.get(`/email/logs?${params.toString()}`);
      setLogs(data.logs || []);
      setStats(data.stats_7d || []);
    } catch (e) {
      console.error("[email/logs]", e);
      toast.error("Failed to load email logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const uniqueTemplates = useMemo(() => Array.from(new Set(logs.map((l) => l.template))).sort(), [logs]);

  const totalsThisWeek = useMemo(() => {
    const sent = stats.reduce((a, b) => a + (b.sent || 0), 0);
    const failed = stats.reduce((a, b) => a + (b.failed || 0), 0);
    return { sent, failed };
  }, [stats]);

  return (
    <div data-testid="email-management-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Email management</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">All transactional emails — view history, resend, and trigger new emails manually.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="wl-btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="email-logs-refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={() => setComposeOpen(true)} className="wl-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="email-compose-btn">
            <Send size={13} /> Send email
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatTile label="Sent (7d)" value={totalsThisWeek.sent} tone="ok" />
        <StatTile label="Failed (7d)" value={totalsThisWeek.failed} tone={totalsThisWeek.failed > 0 ? "warn" : "ok"} />
        <StatTile label="Total in view" value={logs.length} />
        <StatTile label="Templates" value={uniqueTemplates.length} />
      </div>

      {/* Filters */}
      <div className="wl-card-solid p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[11px] font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">Template</label>
          <select value={filter.template} onChange={(e) => setFilter((f) => ({ ...f, template: e.target.value }))} className="wl-input w-full" data-testid="email-filter-template">
            <option value="">All templates</option>
            {uniqueTemplates.map((t) => <option key={t} value={t}>{prettyTemplate(t)}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[11px] font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">Recipient email</label>
          <input value={filter.recipient} onChange={(e) => setFilter((f) => ({ ...f, recipient: e.target.value }))} placeholder="someone@example.com" className="wl-input w-full" data-testid="email-filter-recipient" />
        </div>
        <button onClick={load} className="wl-btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="email-filter-apply">
          <Filter size={13} /> Apply
        </button>
        {(filter.template || filter.recipient) && (
          <button onClick={() => { setFilter({ template: "", recipient: "" }); setTimeout(load, 0); }} className="wl-btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="email-filter-clear">
            <X size={13} /> Clear
          </button>
        )}
      </div>

      {/* Log table */}
      <div className="wl-card-solid overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--bg-surface-warm)] text-[var(--text-secondary)] text-[11px] uppercase tracking-wider font-semibold">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Template</th>
                <th className="text-left px-4 py-3">Recipient(s)</th>
                <th className="text-left px-4 py-3">Triggered by</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Subject</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default)]" data-testid="email-logs-table">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-secondary)]">Loading…</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-secondary)]">No emails match the current filter.</td></tr>
              )}
              {!loading && logs.map((l) => (
                <tr key={l.id} className="hover:bg-[var(--bg-page)]/40" data-testid={`email-log-${l.id}`}>
                  <td className="px-4 py-2.5 text-[var(--text-secondary)] whitespace-nowrap">{formatWhen(l.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--brand-tint-soft)] text-[var(--brand)] text-[11px] font-semibold uppercase tracking-wider">
                      {prettyTemplate(l.template)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">{Array.isArray(l.recipients) ? l.recipients.join(", ") : l.recipients}</td>
                  <td className="px-4 py-2.5 text-[var(--text-secondary)]">{l.triggered_by || "—"}</td>
                  <td className="px-4 py-2.5">
                    {l.failed > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><AlertCircle size={12} /> {l.sent} sent / {l.failed} failed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 size={12} /> Delivered ({l.sent})</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--text-secondary)] text-xs max-w-[260px] truncate" title={l.subject}>{l.subject || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {composeOpen && <ComposeModal onClose={() => { setComposeOpen(false); load(); }} />}
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const toneCls = tone === "warn"
    ? "border-amber-300 bg-amber-50"
    : tone === "ok"
      ? "border-emerald-200 bg-emerald-50/40"
      : "border-[var(--border-default)] bg-[var(--bg-surface)]";
  return (
    <div className={`p-4 rounded-xl border ${toneCls}`} data-testid={`email-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)] font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold tracking-tight mt-1">{value}</div>
    </div>
  );
}

function ComposeModal({ onClose }) {
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState("");
  const [templateId, setTemplateId] = useState("invite");
  const [campaignId, setCampaignId] = useState("");
  const [comments, setComments] = useState([]);
  const [selectedCommentIds, setSelectedCommentIds] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/email/users").then((r) => setUsers(r.data.users || [])).catch((e) => console.error("[email/users]", e));
  }, []);

  const tpl = TEMPLATES.find((t) => t.id === templateId);
  const needsCampaign = tpl?.needs?.includes("campaign");
  const needsComments = tpl?.needs?.includes("comments");

  // Fetch only the campaigns this recipient is mapped to (team-linked for brand users; all for admins).
  useEffect(() => {
    if (!needsCampaign || !selectedRecipient) { setCampaigns([]); setCampaignId(""); return; }
    setCampaignsLoading(true);
    api.get(`/email/recipient-campaigns?email=${encodeURIComponent(selectedRecipient)}`)
      .then((r) => {
        setCampaigns(r.data.campaigns || []);
        setCampaignId("");  // force re-pick when recipient changes
      })
      .catch((e) => { console.error("[recipient-campaigns]", e); toast.error("Couldn't load campaigns for this recipient"); })
      .finally(() => setCampaignsLoading(false));
  }, [needsCampaign, selectedRecipient]);

  useEffect(() => {
    if (!needsComments || !campaignId) { setComments([]); setSelectedCommentIds([]); return; }
    api.get(`/campaigns/${campaignId}/comments`)
      .then((r) => {
        const live = (r.data.comments || []).filter((c) => !c.deleted);
        setComments(live);
        if (live.length > 0) setSelectedCommentIds([live[0].id]);
      })
      .catch(() => toast.error("Couldn't load comments"));
  }, [needsComments, campaignId]);

  // For "Resend invite": hide users who've already activated.
  const visibleUsers = useMemo(() => {
    if (templateId !== "invite") return users;
    return users.filter((u) => (u.status || "active") !== "active");
  }, [users, templateId]);

  // If recipient becomes hidden after template switch, clear the selection.
  useEffect(() => {
    if (selectedRecipient && !visibleUsers.some((u) => u.email === selectedRecipient)) {
      setSelectedRecipient("");
    }
  }, [visibleUsers, selectedRecipient]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase().trim();
    if (!q) return visibleUsers;
    return visibleUsers.filter((u) => u.email.toLowerCase().includes(q) || (u.context || "").toLowerCase().includes(q));
  }, [visibleUsers, userSearch]);

  const send = async () => {
    if (!selectedRecipient) { toast.error("Pick a recipient"); return; }
    if (needsCampaign && !campaignId) { toast.error("Pick a campaign"); return; }
    if (needsComments && selectedCommentIds.length === 0) { toast.error("Pick at least one comment"); return; }
    setSending(true);
    try {
      const res = await api.post("/email/send", {
        template: templateId,
        recipient: selectedRecipient,
        campaign_id: needsCampaign ? campaignId : undefined,
        comment_ids: needsComments ? selectedCommentIds : undefined,
      });
      if (res.data.sent) toast.success(`Email sent to ${selectedRecipient}`);
      else toast.warning(res.data.error || "Email queued but provider reports failure — check logs");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="email-compose-modal">
      <div className="wl-card-solid w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-default)]">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-[var(--brand)]" />
            <h2 className="text-base font-semibold">Send a manual email</h2>
          </div>
          <button onClick={onClose} className="wl-btn-ghost p-1" data-testid="email-compose-close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Template</label>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="wl-input w-full mt-1.5" data-testid="email-compose-template">
              {TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <div className="text-[11px] text-[var(--text-secondary)] mt-1">Templates are standardized — only the recipient and (where applicable) campaign / comment are configurable.</div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Recipient</label>
            <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 mb-1.5">Limited to existing admins and team-mapped users — new users must be invited via <strong>Brands &amp; Teams</strong>.</div>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search by email or brand…" className="wl-input w-full pl-8" data-testid="email-compose-user-search" />
            </div>
            <div className="mt-2 border border-[var(--border-default)] rounded-lg divide-y divide-[var(--border-default)] max-h-48 overflow-y-auto bg-[var(--bg-surface)]">
              {filteredUsers.length === 0 && (
                <div className="p-3 text-xs text-[var(--text-secondary)]" data-testid="email-compose-users-empty">
                  {templateId === "invite"
                    ? "No pending invites — every user has already activated their account. Add a new user in Brands & Teams to send a fresh invite."
                    : "No matching users."}
                </div>
              )}
              {filteredUsers.map((u) => (
                <button
                  key={u.email}
                  type="button"
                  onClick={() => setSelectedRecipient(u.email)}
                  className={`w-full text-left p-2.5 flex items-center justify-between gap-2 transition ${selectedRecipient === u.email ? "bg-[var(--brand-tint-soft)]" : "hover:bg-[var(--bg-page)]"}`}
                  data-testid={`email-compose-user-${u.email}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.email}</div>
                    <div className="text-[11px] text-[var(--text-secondary)] truncate">{u.context}</div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1.5">
                    {u.status && u.status !== "active" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800">{u.status}</span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${u.kind === "admin" ? "bg-[var(--brand-tint-soft)] text-[var(--brand)]" : "bg-blue-100 text-blue-700"}`}>
                      {u.kind}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            {selectedRecipient && (
              <div className="mt-2 text-xs text-[var(--text-primary)]">Selected: <strong>{selectedRecipient}</strong></div>
            )}
          </div>

          {needsCampaign && (
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Campaign</label>
              {!selectedRecipient ? (
                <div className="wl-input w-full mt-1.5 text-xs text-[var(--text-secondary)] italic" data-testid="email-compose-campaign-empty">Pick a recipient first to see their mapped campaigns.</div>
              ) : campaignsLoading ? (
                <div className="wl-input w-full mt-1.5 text-xs text-[var(--text-secondary)]">Loading campaigns…</div>
              ) : campaigns.length === 0 ? (
                <div className="wl-input w-full mt-1.5 text-xs text-amber-700" data-testid="email-compose-campaign-none">No campaigns are mapped to this recipient yet. Map one in <strong>Brands &amp; Teams</strong> first.</div>
              ) : (
                <>
                  <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className="wl-input w-full mt-1.5" data-testid="email-compose-campaign">
                    <option value="">— pick a campaign —</option>
                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.campaign_name} · {c.brand?.name || ""}</option>)}
                  </select>
                  <div className="text-[11px] text-[var(--text-secondary)] mt-1">Showing only campaigns mapped to <strong>{selectedRecipient}</strong>.</div>
                </>
              )}
            </div>
          )}

          {needsComments && campaignId && (
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide flex items-center justify-between">
                <span>Highlight comments</span>
                <span className="text-[10px] tracking-normal normal-case">{selectedCommentIds.length} selected</span>
              </label>
              <div className="mt-2 border border-[var(--border-default)] rounded-lg divide-y divide-[var(--border-default)] max-h-44 overflow-y-auto bg-[var(--bg-surface)]">
                {comments.length === 0 && <div className="p-3 text-xs text-[var(--text-secondary)]">No comments on this campaign yet.</div>}
                {comments.map((c) => {
                  const checked = selectedCommentIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCommentIds((p) => p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id])}
                      className={`w-full text-left p-2.5 transition ${checked ? "bg-[var(--brand-tint-soft)]" : "hover:bg-[var(--bg-page)]"}`}
                      data-testid={`email-compose-comment-${c.id}`}
                    >
                      <div className="text-[11px] font-semibold text-[var(--text-primary)]">{c.user_email}</div>
                      <div className="text-xs text-[var(--text-secondary)] line-clamp-2 mt-0.5">{c.comment_text}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[var(--border-default)] flex justify-end gap-2">
          <button onClick={onClose} className="wl-btn-ghost text-sm" data-testid="email-compose-cancel">Cancel</button>
          <button onClick={send} disabled={sending || !selectedRecipient} className="wl-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="email-compose-send">
            <Send size={13} /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
